/* ============================================================
   MaterialsChips — PF-4 (TASK-74482edf): material chip building
   blocks for the provider cabinet.

   - MaterialChipsSelector: multi-select quick-assign chips for the
     add/edit printer form (spec §5.2) + a technology-aware preset
     button («Быстрый выбор»); selection is a plain id set, the
     material catalog stays the single source of truth.
   - MaterialChip: presentational pill (selected / removable /
     addable variants) reused by the printer card material editor.

   Pure view — state lives in the callers. Light theme pills per
   DESIGN-SYSTEM.md (no gradients, no heavy shadows).
   ============================================================ */

import type { Material, MaterialType } from '../types';
import { applyPreset, materialChips, presetTypesForTechnology, toggleMaterial } from './materialsChipsView';
import type { MaterialIdSet } from './materialsChipsView';
import type { PrinterTechnology } from '../types/provider';

export function MaterialChipsSelector({
  materials, selected, onChange, technology,
}: {
  materials: Material[];
  selected: MaterialIdSet;
  onChange: (next: MaterialIdSet) => void;
  technology: PrinterTechnology;
}) {
  const chips = materialChips(materials, selected);
  const preset = presetTypesForTechnology(technology);
  const presetAvailable = materials.some((m) => m.isActive && preset.includes(m.type));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
          Материалы
        </span>
        {presetAvailable && (
          <button
            type="button"
            className="btn btn-tertiary"
            style={{ height: 26, padding: '2px 10px', fontSize: 'var(--fs-caption)' }}
            onClick={() => onChange(applyPreset(materials, technology))}
          >
            Быстрый выбор ({presetLabel(preset)})
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        {chips.map((c) => (
          <button
            key={c.materialId}
            type="button"
            onClick={() => onChange(toggleMaterial(selected, c.materialId))}
            style={{
              padding: '6px 12px',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--fs-caption)',
              fontWeight: c.selected ? 'var(--fw-semibold)' : 'var(--fw-medium)',
              cursor: 'pointer',
              border: `1px solid ${c.selected ? 'var(--color-primary)' : 'var(--color-separator)'}`,
              background: c.selected ? 'rgba(255,149,0,0.08)' : 'var(--color-surface)',
              color: c.selected ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            }}
          >
            {c.name}
          </button>
        ))}
      </div>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
        Не выбран ни один материал — принтер сможет печатать любым материалом.
      </p>
    </div>
  );
}

export function MaterialChip({
  name, onClick, title, kind,
}: {
  name: string;
  onClick?: () => void;
  title?: string;
  kind: 'linked' | 'removable' | 'addable';
}) {
  const addable = kind === 'addable';
  const removable = kind === 'removable';
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={!onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 'var(--fs-caption)',
        fontWeight: 'var(--fw-medium)',
        cursor: onClick ? 'pointer' : 'default',
        border: addable ? '1px dashed var(--color-separator)' : 'none',
        background: addable ? 'transparent' : 'rgba(255,149,0,0.08)',
        color: addable ? 'var(--color-text-secondary)' : 'var(--color-primary)',
        opacity: addable ? 0.85 : 1,
      }}
    >
      {addable ? `+ ${name}` : name}
      {removable ? ' ×' : ''}
    </button>
  );
}

const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  Pla: 'PLA',
  Petg: 'PETG',
  Abs: 'ABS',
  Tpu: 'TPU',
  ResinStandard: 'Смола',
  ResinEngineering: 'Смола Pro',
  Nylon: 'Nylon',
};

function presetLabel(preset: MaterialType[]): string {
  return preset
    .map((t) => MATERIAL_TYPE_LABELS[t])
    .join(' + ');
}
